/**
 * @element qds-card-advanced
 * 
 * An advanced card component with image/media section, title, description, and action button.
 * 
 * @attr {string} title - The card title text
 * @attr {string} description - The card description text
 * @attr {string} image - URL to the image to display in the card
 * @attr {string} button-text - Text for the action button
 * @attr {string} href - URL that the card button links to
 * @attr {string} target - Where to open the link (_blank, _self, etc.)
 * @attr {string} image-position - Position of the image (top, bottom, left, right)
 * 
 * @csspart card - The main card container
 * @csspart media - The media/image section
 * @csspart image - The image element
 * @csspart content - The card content section
 * @csspart title - The title element
 * @csspart description - The description element
 * @csspart footer - The card footer section
 * @csspart button - The action button
 * 
 * @fires click - Fired when the card button is clicked
 */

import { loadBaseCSS } from '../shared/button-utils.js';
import { applyTypographyStyle } from '../shared/typography-utils.js';

// Custom event interface
interface CardAdvancedClickEventDetail {
  href: string;
}

// Load the base CSS
loadBaseCSS();

export class QdsCardAdvanced extends HTMLElement {
  // Private properties
  private _title: string = '';
  private _description: string = '';
  private _image: string = '';
  private _buttonText: string = 'More';
  private _href: string = '#';
  private _target: string = '_self';
  private _imagePosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
  private _typography: Record<string, unknown> = {};
  private _boundClickHandler?: EventListener;

  static get observedAttributes(): string[] {
    return ['title', 'description', 'image', 'button-text', 'href', 'target', 'image-position', 'typography'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.render();
  }
  
  // Lifecycle callbacks
  connectedCallback(): void {
    if (!this.isConnected) return;
    this._attachClickHandler();
  }

  /**
   * Attaches the click handler to the current button node using a single
   * stable bound reference. Idempotent (removes before adding) so repeated
   * calls from render()/connectedCallback cannot accumulate duplicate
   * listeners.
   * @private
   */
  private _attachClickHandler(): void {
    if (!this.isConnected || !this.shadowRoot) return;
    this._boundClickHandler = this._boundClickHandler || ((event: Event) => this._handleClick(event as MouseEvent));
    const button = this.shadowRoot.querySelector('.card-button');
    if (button) {
      button.removeEventListener('click', this._boundClickHandler);
      button.addEventListener('click', this._boundClickHandler);
    }
  }

  disconnectedCallback(): void {
    // Clean up event listeners
    const button = this.shadowRoot?.querySelector('.card-button');
    if (button && this._boundClickHandler) {
      button.removeEventListener('click', this._boundClickHandler);
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'title':
        this._title = newValue || '';
        break;
      case 'description':
        this._description = newValue || '';
        break;
      case 'image':
        this._image = newValue || '';
        break;
      case 'button-text':
        this._buttonText = newValue || 'More';
        break;
      case 'href':
        this._href = newValue || '#';
        break;
      case 'target':
        this._target = newValue || '_self';
        break;
      case 'image-position':
        this._imagePosition = ['top', 'bottom', 'left', 'right'].includes(newValue)
          ? newValue as 'top' | 'bottom' | 'left' | 'right'
          : 'top';
        break;
      case 'typography':
        this._typography = this._parseTypographyAttribute(newValue);
        break;
    }

    this.render();
  }

  // Getters and setters
  get title(): string {
    return this._title;
  }
  
  set title(value: string) {
    this.setAttribute('title', value);
  }
  
  get description(): string {
    return this._description;
  }
  
  set description(value: string) {
    this.setAttribute('description', value);
  }
  
  get image(): string {
    return this._image;
  }
  
  set image(value: string) {
    this.setAttribute('image', value);
  }
  
  get buttonText(): string {
    return this._buttonText;
  }
  
  set buttonText(value: string) {
    this.setAttribute('button-text', value);
  }
  
  get href(): string {
    return this._href;
  }
  
  set href(value: string) {
    this.setAttribute('href', value);
  }
  
  get target(): string {
    return this._target;
  }
  
  set target(value: string) {
    this.setAttribute('target', value);
  }
  
  get imagePosition(): 'top' | 'bottom' | 'left' | 'right' {
    return this._imagePosition;
  }
  
  set imagePosition(value: 'top' | 'bottom' | 'left' | 'right') {
    this.setAttribute('image-position', value);
  }

  get typography(): Record<string, unknown> {
    return this._typography;
  }

  set typography(value: Record<string, unknown> | undefined) {
    this._typography = value ? { ...value } : {};
    this.render();
  }

  /**
   * Parses a JSON typography attribute value into a style map. Invalid JSON
   * is ignored (falls back to an empty map) so a malformed attribute cannot
   * throw during attribute processing.
   * @private
   */
  private _parseTypographyAttribute(value: string | null): Record<string, unknown> {
    if (!value) {
      return {};
    }
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }

  // Event handlers
  private _handleClick(event: MouseEvent): void {
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent<CardAdvancedClickEventDetail>('click', {
      bubbles: true,
      composed: true,
      detail: { href: this._href }
    }));
  }

  // Render the component
  private render(): void {
    if (!this.shadowRoot) return;
    
    const hasImage = !!this._image;
    const imageOnTop = this._imagePosition === 'top';
    const imageOnBottom = this._imagePosition === 'bottom';
    const imageOnLeft = this._imagePosition === 'left';
    const imageOnRight = this._imagePosition === 'right';
    const usesSplitLayout = imageOnLeft || imageOnRight;
    
    const styles = `
      :host {
        display: block;
        --card-font-family: var(--qds-card-font-family, var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif)));
        --card-bg-color: var(--qds-card-bg-color, var(--theme-background-primary));
        --card-text-color: var(--qds-card-text-color, var(--theme-text-primary));
        --card-muted-text-color: var(--qds-card-muted-text-color, var(--theme-text-secondary));
        --card-border-color: var(--qds-card-border-color, var(--theme-border-primary));
        --card-border-radius: var(--qds-card-radius, var(--theme-radius-md, 8px));
        --card-base-shadow: var(--qds-card-shadow, var(--theme-shadow-xs));
        --card-hover-shadow: var(--qds-card-hover-shadow, var(--theme-shadow-sm));
        --card-accent-color: var(--qds-accent-color, var(--theme-accent-primary));
        --card-image-height: var(--qds-card-image-height, 180px);
        --card-title-font-size: var(--qds-card-title-font-size, var(--theme-font-size-lg, 18px));
        --card-body-font-size: var(--qds-card-body-font-size, var(--theme-font-size-sm, 14px));
        --card-action-font-size: var(--qds-card-action-font-size, var(--theme-font-size-sm, 14px));
        --card-title-font-weight: var(--qds-card-title-font-weight, var(--theme-font-weight-semibold, 600));
        --card-action-font-weight: var(--qds-card-action-font-weight, var(--theme-font-weight-medium, 500));
        --card-content-padding: var(--qds-card-content-padding, var(--theme-space-4, 16px));
        --card-content-gap: var(--qds-card-content-gap, var(--theme-space-2, 8px));
        --card-description-margin-bottom: var(--qds-card-description-margin-bottom, var(--theme-space-4, 16px));
        --card-button-icon-gap: var(--qds-card-button-icon-gap, var(--theme-space-1, 4px));
        --card-arrow-icon-size: var(--qds-card-arrow-icon-size, calc(var(--icon-size, 20px) - var(--theme-space-1, 4px)));
        --card-button-radius: var(--qds-card-button-radius, var(--theme-radius-sm, 4px));
        --card-min-height: var(--qds-card-min-height, 400px);
        --card-placeholder-start-color: var(--qds-card-placeholder-start-color, var(--theme-background-secondary));
        --card-placeholder-end-color: var(--qds-card-placeholder-end-color, var(--theme-background-accent-subtle));
        --card-placeholder-icon-color: var(--qds-card-placeholder-icon-color, var(--theme-accent-secondary));
        --card-placeholder-icon-size: var(--qds-card-placeholder-icon-size, 48px);
      }
      
      .card {
        display: flex;
        flex-direction: column;
        font-family: var(--card-font-family);
        background-color: var(--card-bg-color);
        color: var(--card-text-color);
        border-radius: var(--card-border-radius);
        overflow: hidden;
        box-shadow: var(--card-base-shadow);
        transition: box-shadow var(--transition-speed-normal, 0.25s) ease;
        height: 100%;
        min-height: var(--card-min-height);
        border: 1px solid var(--card-border-color);
      }

      .card--split {
        flex-direction: row;
        align-items: stretch;
        min-height: clamp(280px, 28vw, 360px);
      }

      .card--image-right {
        flex-direction: row-reverse;
      }
      
      .card:hover {
        box-shadow: var(--card-hover-shadow);
      }
      
      .card-media {
        height: var(--card-image-height);
        overflow: hidden;
        position: relative;
      }

      .card--split .card-media {
        width: 50%;
        height: auto;
        min-height: clamp(260px, 32vw, 420px);
        flex: 1 1 50%;
      }
      
      .card-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
      }
      
      .card-content {
        padding: var(--card-content-padding);
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        gap: var(--card-content-gap);
        background-color: var(--card-bg-color);
      }

      .card--split .card-content {
        flex: 1 1 50%;
        justify-content: center;
      }

      .card--split .card-description {
        flex-grow: 0;
        margin-bottom: 0;
      }

      .card--split .card-footer {
        margin-top: 16px;
      }
      
      .card-title {
        font-size: var(--card-title-font-size);
        font-weight: var(--card-title-font-weight);
        margin: 0;
        color: var(--card-text-color);
      }
      
      .card-description {
        font-size: var(--card-body-font-size);
        color: var(--card-muted-text-color);
        margin: 0 0 var(--card-description-margin-bottom) 0;
        line-height: 1.5;
        flex-grow: 1;
      }
      
      .card-footer {
        padding: 0;
        display: flex;
        justify-content: flex-end;
        align-items: center;
      }
      
      .card-button {
        display: inline-flex;
        align-items: center;
        gap: var(--card-button-icon-gap);
        background: none;
        border: none;
        color: var(--card-accent-color);
        font-size: var(--card-action-font-size);
        font-weight: var(--card-action-font-weight);
        cursor: pointer;
        padding: 0;
        text-decoration: none;
      }

      .card-button:visited {
        color: var(--card-accent-color);
        text-decoration: none;
      }
      
      .card-button:hover {
        text-decoration: underline;
      }

      .card-button:focus-visible {
        outline: 2px solid var(--card-accent-color);
        outline-offset: 2px;
        border-radius: var(--card-button-radius);
        box-shadow: 0 0 0 4px var(--button-focus-ring);
      }
      
      .arrow-icon {
        width: var(--card-arrow-icon-size);
        height: var(--card-arrow-icon-size);
      }
      
      /* Placeholder style when no image is provided */
      .placeholder {
        width: 100%;
        height: 100%;
        background: linear-gradient(
          135deg,
          var(--card-placeholder-start-color) 0%,
          var(--card-placeholder-end-color) 100%
        );
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .placeholder-icon {
        width: var(--card-placeholder-icon-size);
        height: var(--card-placeholder-icon-size);
        color: var(--card-placeholder-icon-color);
        opacity: 0.24;
      }

      @media (max-width: 900px) {
        .card--split,
        .card--image-right {
          flex-direction: column;
        }

        .card--split .card-media {
          width: 100%;
          height: var(--card-image-height);
          min-height: 0;
        }
      }
    `;

    const mediaMarkup = hasImage
      ? `
          <div class="card-media" part="media">
            <img class="card-image" src="${this._image}" alt="${this._title || 'Card image'}" part="image">
          </div>
        `
      : `
          <div class="card-media" part="media">
            <div class="placeholder">
              <svg class="placeholder-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
            </div>
          </div>
        `;
    const contentMarkup = `
      <div class="card-content" part="content">
        <h3 class="card-title" part="title">${this._title}</h3>
        <p class="card-description" part="description">${this._description}</p>
        <div class="card-footer" part="footer">
          <a class="card-button" href="${this._href}" target="${this._target}" part="button">
            ${this._buttonText}
            <svg class="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"></path>
            </svg>
          </a>
        </div>
      </div>
    `;

    let cardLayout = '';
    if (usesSplitLayout) {
      cardLayout = `${mediaMarkup}${contentMarkup}`;
    } else if (imageOnBottom) {
      cardLayout = `${contentMarkup}${hasImage ? mediaMarkup : ''}`;
    } else {
      cardLayout = `${mediaMarkup}${contentMarkup}`;
    }

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>
      <div class="card ${usesSplitLayout ? `card--split${imageOnRight ? ' card--image-right' : ''}` : ''}" part="card">
        ${cardLayout}
      </div>
    `;

    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.card-title'), this._typography.title);
    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.card-description'), this._typography.description);
    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.card-button'), this._typography.buttonText);

    this._attachClickHandler();
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-card-advanced')) {
  customElements.define('qds-card-advanced', QdsCardAdvanced);
}

// Export default for direct import
export default QdsCardAdvanced;
